/**
 * SF-4762 — client for the sf-api platform feature flags endpoints.
 *
 *   GET /admin/platform-feature-flags              — admin:platform:flags:read
 *   PUT /admin/platform-feature-flags/:key         — admin:platform:flags:write
 *       body { enabled?: boolean, exemptEmails?: string[] }
 *   GET /admin/platform-feature-flags/:key/history — admin:platform:flags:read
 *
 * Platform flags are global per environment. sf-api wraps every response in
 * `{ success, data }`; the data provider's `custom` unwraps it, so hooks expose
 * the payload at `query.data?.data` (same as evaPlatformDefaultsClient).
 */
import { useMemo } from 'react';
import { useCustom, useCustomMutation, usePermissions } from '@refinedev/core';
import { hasPermission } from '../../../utils/permissions';

export const PLATFORM_FLAGS_BASE = 'admin/platform-feature-flags';
export const PLATFORM_FLAGS_READ_PERMISSION = 'admin:platform:flags:read';
export const PLATFORM_FLAGS_WRITE_PERMISSION = 'admin:platform:flags:write';

/** sf-api refuses more than 50 exempt emails per flag (400 INVALID_BODY). */
export const MAX_EXEMPT_EMAILS = 50;

export interface FlagActor {
  id: string;
  email: string;
}

export interface PlatformFlag {
  key: string;
  label: string;
  description: string;
  /** The company boolean this flag also switches on, or null for platform-only. */
  companyField: string | null;
  exemptAccounts: boolean;
  /**
   * SF-4801 — the flag's own name for its email list (e.g. "Addresses that get
   * real email"). Absent on older sf-api and on flags whose list is an exempt list.
   */
  listLabel?: string | null;
  /** SF-4801 — one line under the list heading; only used with listLabel. */
  listHelp?: string | null;
  enabled: boolean;
  exemptEmails: string[];
  updatedBy: FlagActor | null;
  updatedAt: string | null;
}

export interface PlatformFlagChange {
  field: 'enabled' | 'exemptEmails';
  old: unknown;
  new: unknown;
  changedBy: FlagActor | null;
  createdAt: string;
}

export interface PlatformFlagUpdate {
  enabled?: boolean;
  exemptEmails?: string[];
}

/**
 * List every platform flag (registry order).
 * @param options.enabled - gate on the read permission so no doomed 403 fires
 */
export function usePlatformFlags(options: { enabled?: boolean } = {}) {
  return useCustom<PlatformFlag[]>({
    url: `/${PLATFORM_FLAGS_BASE}`,
    method: 'get',
    queryOptions: {
      enabled: options.enabled ?? true,
      // A stale list from another screen must never grey or ungrey a switch.
      keepPreviousData: false,
      refetchOnWindowFocus: false,
    },
    // Callers show read failures inline (or, on the company tab, not at all).
    errorNotification: false,
  });
}

/**
 * Company boolean fields that a switched-on platform flag turns on for every
 * company (the company Feature Flags tab greys those switches). Empty while
 * loading, on any error, or without the read permission, so the tab then
 * behaves exactly as before: we never grey a switch on a guess.
 */
export function usePlatformOnCompanyFields(): ReadonlySet<string> {
  const { data: permissions, isLoading: permsLoading } = usePermissions<string[]>();
  const canRead = !permsLoading && hasPermission(permissions ?? [], PLATFORM_FLAGS_READ_PERMISSION);
  const query = usePlatformFlags({ enabled: canRead });
  const flags = query.isError ? undefined : query.data?.data;
  return useMemo(
    () =>
      new Set(
        (Array.isArray(flags) ? flags : [])
          .filter((f) => f.enabled === true && typeof f.companyField === 'string')
          .map((f) => f.companyField as string),
      ),
    [flags],
  );
}

/** Query key prefix of every flag's history, so the page Refresh can reload them all. */
export const PLATFORM_FLAG_HISTORY_QUERY_KEY = ['sf-admin', 'platform-flag-history'] as const;

/** The change history of one flag, newest first. */
export function usePlatformFlagHistory(key: string, options: { enabled?: boolean } = {}) {
  return useCustom<PlatformFlagChange[]>({
    url: `/${PLATFORM_FLAGS_BASE}/${encodeURIComponent(key)}/history`,
    method: 'get',
    queryOptions: {
      queryKey: [...PLATFORM_FLAG_HISTORY_QUERY_KEY, key],
      enabled: options.enabled ?? true,
      refetchOnWindowFocus: false,
    },
    errorNotification: false,
  });
}

export function useUpdatePlatformFlag() {
  return useCustomMutation<PlatformFlag>();
}

/**
 * PUT args for one flag write. Shared by the page and tests.
 * @param key - registry key of the flag
 * @param body - at least one of enabled / exemptEmails
 */
export function platformFlagUpdateMutationArgs(key: string, body: PlatformFlagUpdate) {
  return {
    url: `/${PLATFORM_FLAGS_BASE}/${encodeURIComponent(key)}`,
    method: 'put' as const,
    values: body,
    errorNotification: false as const,
    successNotification: false as const,
  };
}

/**
 * A loose something@domain.tld shape check so a typo gets an instant, clear
 * message. sf-api is the authority (400 INVALID_BODY) and stores the email
 * normalised; we normalise the same way (trim + lower case, like sf-api's
 * normalizeEmail) so the list on screen matches what is stored.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normaliseEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

export function isValidEmail(raw: string): boolean {
  return EMAIL_PATTERN.test(normaliseEmail(raw));
}
